import { useState, useEffect } from "react"
import { Loader2, Search, ClipboardList, MessageSquare, Check, X, Users, Target, Medal } from "lucide-react"
import CoachSidebar from "../components/CoachSidebar"
import EmptyState from "../components/EmptyState"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function CoachAthletes() {
  const [athletes, setAthletes] = useState([])
  const [search, setSearch] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const { t } = useLanguage()

  const [selectedIds, setSelectedIds] = useState(new Set())
  const [actionModal, setActionModal] = useState(null) // "task" | "note"
  const [goalsModalSwimmerId, setGoalsModalSwimmerId] = useState(null)
  const [swimmerGoals, setSwimmerGoals] = useState([])
  const [isFetchingGoals, setIsFetchingGoals] = useState(false)
  const [formData, setFormData] = useState({ task: "", duration: "", note: "" })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [actionError, setActionError] = useState("")
  const [actionSuccess, setActionSuccess] = useState("")

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  async function fetchAthletes() {
    setIsLoading(true)
    try {
      const res = await fetch(`${API}/api/coach/my-swimmers`, { headers: authHeaders() })
      if (!res.ok) throw new Error("Failed to fetch athletes")
      setAthletes(await res.json())
    } catch (e) {
      setError(e.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchAthletes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filteredAthletes = athletes.filter(a => 
    a.full_name.toLowerCase().includes(search.toLowerCase()) || 
    a.id.toLowerCase().includes(search.toLowerCase())
  )

  const toggleSelect = (id) => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedIds(next)
  }

  const selectAll = () => {
    if (selectedIds.size === filteredAthletes.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredAthletes.map(a => a.id)))
    }
  }

  const handleViewGoals = async () => {
    if (selectedIds.size !== 1) return
    const id = Array.from(selectedIds)[0]
    setGoalsModalSwimmerId(id)
    setIsFetchingGoals(true)
    try {
      const res = await fetch(`${API}/api/coach/swimmers/${id}/goals`, { headers: authHeaders() })
      if (!res.ok) throw new Error("Failed to fetch goals")
      setSwimmerGoals(await res.json())
    } catch (e) {
      console.error(e)
    } finally {
      setIsFetchingGoals(false)
    }
  }

  async function handleSubmitAction(e) {
    e.preventDefault()
    setActionError("")
    if (selectedIds.size === 0) { setActionError("Select at least one swimmer"); return }

    setIsSubmitting(true)
    try {
      const endpoint = actionModal === "task" ? "/api/coach/workouts/bulk" : "/api/coach/notes/bulk"
      const body = actionModal === "task"
        ? { task: formData.task, duration_min: Number(formData.duration), swimmer_ids: Array.from(selectedIds) }
        : { note: formData.note, swimmer_ids: Array.from(selectedIds) }

      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body)
      })

      if (!res.ok) throw new Error("Failed to assign")
      const count = selectedIds.size
      setActionModal(null)
      setFormData({ task: "", duration: "", note: "" })
      setSelectedIds(new Set())
      setActionSuccess(`Assigned to ${count} swimmer${count === 1 ? "" : "s"}.`)
    } catch (err) {
      setActionError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <CoachSidebar />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10 relative">
        <header className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("coach_athletes_title")}</h1>
            <p className="mt-1 text-sm text-mist-300">{t("coach_athletes_subtitle")}</p>
          </div>
          <div className="flex gap-3">
            <button onClick={handleViewGoals} disabled={selectedIds.size !== 1} className="flex items-center gap-2 rounded-lg bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 px-4 py-2 text-sm font-bold disabled:opacity-50 hover:bg-indigo-500 hover:text-white transition-colors">
              <Target className="h-4 w-4" /> {t("coach_athletes_view_goals")}
            </button>
            <button onClick={() => setActionModal("task")} disabled={selectedIds.size === 0} className="flex items-center gap-2 rounded-lg bg-gold-500 px-4 py-2 text-sm font-bold text-ink-900 disabled:opacity-50 hover:bg-gold-400">
              <ClipboardList className="h-4 w-4" /> {t("coach_athletes_assign")}
            </button>
            <button onClick={() => setActionModal("note")} disabled={selectedIds.size === 0} className="flex items-center gap-2 rounded-lg bg-ink-800 border border-white/20 px-4 py-2 text-sm font-bold text-mist-300 disabled:opacity-50 hover:bg-white/5 hover:text-paper-0">
              <MessageSquare className="h-4 w-4" /> {t("coach_athletes_send_note")}
            </button>
          </div>
        </header>

        {error && <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}
        {actionSuccess && <div className="mb-6 rounded border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">{actionSuccess}</div>}

        <div className="mb-6 flex items-center gap-4">
          <div className="flex flex-1 items-center gap-3 rounded-lg border border-white/10 bg-ink-800/50 px-4 py-2.5">
            <Search className="h-4 w-4 text-mist-500" />
            <input 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
              placeholder={t("coach_athletes_search")}
              className="bg-transparent text-sm text-paper-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60 rounded flex-1"
            />
          </div>
          <p className="text-sm font-medium text-mist-400">
            {selectedIds.size} {t("coach_athletes_selected")}
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold-400" /></div>
        ) : (
          <div className="rounded-xl border border-white/10 bg-ink-800/30 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-white/10 bg-ink-800/80 text-xs font-semibold uppercase tracking-wider text-mist-400">
                <tr>
                  <th className="px-6 py-4 w-12 text-center">
                    <button onClick={selectAll} className={`flex h-5 w-5 items-center justify-center rounded border ${selectedIds.size === filteredAthletes.length && filteredAthletes.length > 0 ? 'bg-gold-500 border-gold-500 text-ink-900' : 'border-mist-500/50 bg-transparent'}`}>
                      {selectedIds.size === filteredAthletes.length && filteredAthletes.length > 0 && <Check className="h-3.5 w-3.5" />}
                    </button>
                  </th>
                  <th className="px-6 py-4">{t("coach_athletes_col_swimmer")}</th>
                  <th className="px-6 py-4">{t("coach_athletes_col_age")}</th>
                  <th className="px-6 py-4">{t("coach_athletes_col_dob")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredAthletes.map(a => {
                  const isSelected = selectedIds.has(a.id)
                  return (
                    <tr
                      key={a.id}
                      onClick={() => toggleSelect(a.id)}
                      role="checkbox"
                      aria-checked={isSelected}
                      tabIndex={0}
                      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleSelect(a.id) } }}
                      className={`transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60 focus-visible:ring-inset ${isSelected ? 'bg-gold-500/5 hover:bg-gold-500/10' : 'hover:bg-white/[0.02]'}`}
                    >
                      <td className="px-6 py-4 text-center">
                        <div className={`mx-auto flex h-5 w-5 items-center justify-center rounded border transition-colors ${isSelected ? 'bg-gold-500 border-gold-500 text-ink-900' : 'border-mist-500/50 bg-transparent text-transparent'}`}>
                          <Check className="h-3.5 w-3.5" />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gold-500/20 font-bold text-gold-400">
                            {a.avatar_initials}
                          </div>
                          <div>
                            <p className="font-semibold text-paper-0">{a.full_name}</p>
                            <p className="text-[11px] text-mist-500 font-mono mt-0.5">{a.id.slice(0, 12)}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-mist-300 font-medium">{a.age_group}</td>
                      <td className="px-6 py-4 text-mist-400">{a.dob}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            </div>
            {filteredAthletes.length === 0 && (
              <EmptyState
                icon={<Users className="h-12 w-12 text-mist-600" />}
                title={t("coach_athletes_empty")}
                hint={search ? t("coach_athletes_empty_hint1") : t("coach_athletes_empty_hint2")}
              />
            )}
          </div>
        )}
      </main>

      {/* Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-ink-800 p-8 shadow-2xl">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-paper-0">
                {actionModal === "task" ? t("coach_athletes_modal_task") : t("coach_athletes_modal_note")}
              </h2>
              <button onClick={() => setActionModal(null)} className="text-mist-400 hover:text-paper-0"><X className="h-5 w-5" /></button>
            </div>
            
            <form onSubmit={handleSubmitAction} className="flex flex-col gap-4">
              <p className="text-sm font-medium text-mist-300 bg-white/5 p-3 rounded mb-2">
                {t("coach_athletes_assigning")} <strong className="text-gold-400">{selectedIds.size}</strong> {t("coach_athletes_swimmers")}
              </p>

              {actionModal === "task" ? (
                <>
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("coach_athletes_task_details")}</label>
                    <input required value={formData.task} onChange={e => setFormData({...formData, task: e.target.value})} placeholder="e.g. 10x100m Freestyle @ 1:30" className="w-full rounded border border-white/10 bg-ink-900/50 p-3 text-sm text-paper-0 focus:border-gold-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("coach_athletes_duration")}</label>
                    <input required type="number" value={formData.duration} onChange={e => setFormData({...formData, duration: e.target.value})} className="w-full rounded border border-white/10 bg-ink-900/50 p-3 text-sm text-paper-0 focus:border-gold-500 focus:outline-none" />
                  </div>
                </>
              ) : (
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("coach_athletes_msg_content")}</label>
                  <textarea required value={formData.note} onChange={e => setFormData({...formData, note: e.target.value})} placeholder={t("coach_athletes_write_note")} rows={4} className="w-full rounded border border-white/10 bg-ink-900/50 p-3 text-sm text-paper-0 focus:border-gold-500 focus:outline-none" />
                </div>
              )}

              {actionError && <p className="text-sm font-medium text-red-300">{actionError}</p>}

              <div className="mt-4 flex justify-end gap-3 pt-4 border-t border-white/10">
                <button type="button" onClick={() => setActionModal(null)} className="rounded px-4 py-2 text-sm font-semibold text-mist-300 hover:text-paper-0">{t("common_cancel")}</button>
                <button type="submit" disabled={isSubmitting} className="flex items-center gap-2 rounded bg-gold-500 px-6 py-2 text-sm font-bold text-ink-900 hover:bg-gold-400 disabled:opacity-50">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {t("coach_athletes_confirm_send")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Goals Modal */}
      {goalsModalSwimmerId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl border border-white/10 bg-ink-800 p-8 shadow-2xl">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="font-display text-xl font-bold text-paper-0">{t("coach_athletes_goals_title")}</h2>
                <p className="text-sm text-mist-300 mt-1">{t("coach_athletes_goals_sub")}</p>
              </div>
              <button onClick={() => setGoalsModalSwimmerId(null)} className="text-mist-400 hover:text-paper-0"><X className="h-5 w-5" /></button>
            </div>
            
            {isFetchingGoals ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold-400" /></div>
            ) : swimmerGoals.length === 0 ? (
              <EmptyState icon={<Target className="h-12 w-12 text-mist-600" />} title={t("coach_athletes_no_goals")} hint={t("coach_athletes_no_goals_hint")} />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {swimmerGoals.map(g => (
                  <div key={g.id} className="rounded-xl border border-white/10 bg-ink-900/50 p-4 relative overflow-hidden shadow-sm">
                    {g.completed && (
                      <div className="absolute top-0 right-0 rounded-bl-xl bg-gold-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gold-400 border-b border-l border-gold-500/30 flex items-center gap-1">
                        <Medal className="h-3 w-3" /> {t("coach_athletes_done")}
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">{g.icon}</span>
                      <h3 className="font-display text-md font-bold text-paper-0 truncate">{g.title}</h3>
                    </div>
                    <div className="flex items-center justify-between text-xs mb-3">
                      <div><span className="text-mist-500 font-bold uppercase">{t("coach_athletes_cur")}</span> <span className="text-mist-300 ml-1">{g.current}</span></div>
                      <div><span className="text-mist-500 font-bold uppercase">{t("coach_athletes_tgt")}</span> <span className="text-gold-400 font-bold ml-1">{g.target}</span></div>
                    </div>
                    <div>
                      <div className="mb-1 flex items-center justify-between text-[10px] font-bold uppercase text-mist-400">
                        <span>{t("goals_progress")}</span><span>{g.progress}%</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink-950 border border-white/5">
                        <div className={`h-full rounded-full ${g.completed ? 'bg-emerald-500/80' : 'bg-gold-500'}`} style={{ width: `${g.progress}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 flex justify-end pt-4 border-t border-white/10">
              <button onClick={() => setGoalsModalSwimmerId(null)} className="rounded px-4 py-2 text-sm font-semibold bg-white/5 text-paper-0 hover:bg-white/10">{t("common_close")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
